/**
 * detectLang.ts — اختيار اللغة الافتراضية حسب **مكان فتح الموقع**.
 *
 * لا نستدعي أي خدمة خارجية (لا GeoIP): المنطقة الزمنية للمتصفّح تحدّد البلد عملياً،
 * وهي متاحة فوراً وبلا طلب شبكة ولا مشاكل خصوصية/حجب. القاعدة:
 *   منطقة زمنية في بلد عربي  ⇒ العربية
 *   غير ذلك                  ⇒ الإنجليزية
 * لغة المتصفّح تُستخدم فقط عندما تتعذّر قراءة المنطقة الزمنية (متصفّح قديم/بيئة مقيَّدة).
 *
 * ترتيب الأولويّة النهائي (في LanguageContext): اختيار المستخدم المحفوظ ⇒ ?lang= ⇒ هذا الكشف.
 */
import type { Lang } from './types';

/**
 * مناطق IANA الزمنية للدول الأعضاء في جامعة الدول العربية (٢٢ دولة).
 * مكتوبة كاملة لا بالبادئة: `Africa/` وحدها تشمل أفريقيا غير العربية أيضاً.
 */
const ARABIC_TIME_ZONES = new Set([
    // الخليج
    'Asia/Riyadh', 'Asia/Kuwait', 'Asia/Qatar', 'Asia/Bahrain', 'Asia/Dubai', 'Asia/Muscat',
    // المشرق
    'Asia/Baghdad', 'Asia/Damascus', 'Asia/Beirut', 'Asia/Amman', 'Asia/Jerusalem', 'Asia/Gaza',
    'Asia/Hebron', 'Asia/Aden',
    // وادي النيل والقرن الأفريقي
    'Africa/Cairo', 'Africa/Khartoum', 'Africa/Juba', 'Africa/Djibouti', 'Africa/Mogadishu',
    'Indian/Comoro',
    // المغرب العربي
    'Africa/Tripoli', 'Africa/Tunis', 'Africa/Algiers', 'Africa/Casablanca', 'Africa/El_Aaiun',
    'Africa/Nouakchott',
]);

/** رموز الدول العربية — تُستعمل عند توفّر `Intl.Locale#region` من لغة المتصفّح. */
const ARABIC_REGIONS = new Set([
    'SA', 'KW', 'QA', 'BH', 'AE', 'OM', 'YE', 'IQ', 'SY', 'LB', 'JO', 'PS',
    'EG', 'SD', 'SS', 'DJ', 'SO', 'KM', 'LY', 'TN', 'DZ', 'MA', 'EH', 'MR',
]);

function timeZoneLang(): Lang | null {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!tz) return null;
        return ARABIC_TIME_ZONES.has(tz) ? 'ar' : 'en';
    } catch {
        return null;
    }
}

function browserLang(): Lang | null {
    try {
        const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
        for (const tag of tags) {
            if (!tag) continue;
            const lower = tag.toLowerCase();
            if (lower.startsWith('ar')) return 'ar';
            // en-SA / fr-MA … : المنطقة أهمّ من اللغة لأن السؤال «من أين فُتح الموقع».
            const region = tag.split('-')[1]?.toUpperCase();
            if (region && ARABIC_REGIONS.has(region)) return 'ar';
        }
        return tags.some((t) => t?.toLowerCase().startsWith('en')) ? 'en' : null;
    } catch {
        return null;
    }
}

/** اللغة الافتراضية لأوّل زيارة — بلا أي طلب شبكة. */
export function detectLang(): Lang {
    return timeZoneLang() ?? browserLang() ?? 'en';
}
